import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Check, X, Loader2 } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SeoHead from '@/components/SeoHead';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isAuthenticated, refreshUser } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  const verifyEmail = useCallback(async () => {
    try {
      await api.verifyEmail(token);
      if (isAuthenticated) {
        await refreshUser();
      }
      setStatus('success');
      setMessage('Your email has been verified successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed or link expired');
    }
  }, [isAuthenticated, refreshUser, token]);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token, verifyEmail]);

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <SeoHead
        title="Verify Email | JOBFITAI"
        description="Verify your JOBFITAI email address to activate account access and resume tools."
        canonicalPath="/verify-email"
        robots="noindex,follow"
      />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex">
            <AppLogo variant="auth" />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white border border-border rounded-xl shadow-soft p-8">
          <div className="text-center">
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Verifying email...</h2>
                <p className="text-muted-foreground">Please wait</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-success" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Email Verified!</h2>
                <p className="text-muted-foreground mb-6">{message}</p>
                <Link
                  to="/dashboard"
                  className="inline-block bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors"
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                </Link>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-destructive" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Verification Failed</h2>
                <p className="text-muted-foreground mb-6">{message}</p>
                <Link
                  to="/login"
                  className="inline-block bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors"
                >
                  Back to Login
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
